import { DatePicker } from "@app/components/DatePicker.tsx";
import useAssignmentData from "@app/data-hooks/use-assignment-data.ts";
import useWorkPackageData from "@app/data-hooks/use-work-package-data.ts";
import {
    Box,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid2,
} from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { CurrencyInput, Dialog, QueryStatusRouter, Select } from "bi-lib";
import moment from "moment";

interface Props {
    open: boolean;
    onClose: () => void;
    id?: number;
    assignmentId: number;
}

export default function AssignmentRatesDefaultModal({ open, onClose, id, assignmentId }: Props) {
    const { assignmentDetail, updateAssignmentDetail } = useAssignmentData({
        assignmentDetailId: open ? id : undefined,
    });
    const { workPackagesListByAssignment } = useWorkPackageData({ assignmentId: open ? assignmentId : undefined });

    const defaultValues: any = {
        effDate: assignmentDetail.data?.effectiveDate
            ? moment(assignmentDetail.data?.effectiveDate)
            : null,
        packageId: assignmentDetail.data?.workPackageId,
        billableTime: assignmentDetail.data?.billableTime,
        billableTravel: assignmentDetail.data?.billableTravelExpense,
        billableIndvExp: assignmentDetail.data?.billableOtherIndividualExpense,
        billableOffExp: assignmentDetail.data?.billableOfficeExpense,
        invVisTime: assignmentDetail.data?.invoiceVisibleTime,
        invVisTravel: assignmentDetail.data?.invoiceVisibleTravelExpense,
        invVisIndvExp: assignmentDetail.data?.invoiceVisibleOtherIndividualExpense,
        invVisOffExp: assignmentDetail.data?.invoiceVisibleOtherIndividualExpense,
        costRt: assignmentDetail.data?.defaultCostRate,
        billingRt: assignmentDetail.data?.defaultBillingRate,
        internalRt: assignmentDetail.data?.defaultInternalRate,
    };

    const form = useForm({
        defaultValues,
        onSubmit: async ({ value }) => {
            await updateAssignmentDetail.mutateAsync({
                id,
                assignmentId,
                effDate: value.effDate?.format("MM/DD/YYYY"),
                packageId: value.packageId,
                billableTime: value.billableTime,
                billableTravel: value.billableTravel,
                billableIndvExp: value.billableIndvExp,
                billableOffExp: value.billableOffExp,
                invVisTime: value.invVisTime,
                invVisTravel: value.invVisTravel,
                invVisIndvExp: value.invVisIndvExp,
                invVisOffExp: value.invVisOffExp,
                costRt: value.costRt,
                billingRt: value.billingRt,
                internalRt: value.internalRt,
            });
            onClose();
        },
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth={"md"} fullWidth>
            <DialogTitle>{id == undefined ? "Create" : "Edit"} Default</DialogTitle>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
            >
                <DialogContent>
                    <Grid2 container spacing={1}>
                        <Grid2 size={6}>
                            <QueryStatusRouter queryHook={workPackagesListByAssignment}>
                                <form.Field name={"packageId"}>
                                    {(field) => (
                                        <Select
                                            label={"Work Package"}
                                            value={field.state.value}
                                            onChange={(e) => field.handleChange(e)}
                                            fullWidth
                                            options={workPackagesListByAssignment.data?.map((x) => ({
                                                label: x.displayName,
                                                value: x.workPackageId,
                                            }))}
                                        />
                                    )}
                                </form.Field>
                            </QueryStatusRouter>
                        </Grid2>
                        <Grid2 size={6}>
                            <form.Field name={"effDate"}>
                                {(field) => (
                                    <DatePicker
                                        value={field.state.value}
                                        label={"Effective Date"}
                                        onChange={(e) => field.handleChange(e)}
                                        sx={{ width: "100%" }}
                                    />
                                )}
                            </form.Field>
                        </Grid2>
                        <Grid2 size={12}>
                            <Box display={"flex"} justifyContent={"space-evenly"} flexWrap={"wrap"}>
                                <form.Field name={"billableTime"}>
                                    {(field) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={field.state.value ?? false}
                                                    onChange={(e) => field.handleChange(e.target.checked)}
                                                />
                                            }
                                            label={"Billable Time"}
                                        />
                                    )}
                                </form.Field>
                                <form.Field name={"billableTravel"}>
                                    {(field) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={field.state.value ?? false}
                                                    onChange={(e) => field.handleChange(e.target.checked)}
                                                />
                                            }
                                            label={"Billable Travel Expenses"}
                                        />
                                    )}
                                </form.Field>
                                <form.Field name={"billableIndvExp"}>
                                    {(field) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={field.state.value ?? false}
                                                    onChange={(e) => field.handleChange(e.target.checked)}
                                                />
                                            }
                                            label={"Billable Individual Expenses"}
                                        />
                                    )}
                                </form.Field>
                                <form.Field name={"billableOffExp"}>
                                    {(field) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={field.state.value ?? false}
                                                    onChange={(e) => field.handleChange(e.target.checked)}
                                                />
                                            }
                                            label={"Billable Office Expenses"}
                                        />
                                    )}
                                </form.Field>
                                <form.Field name={"invVisTime"}>
                                    {(field) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={field.state.value ?? false}
                                                    onChange={(e) => field.handleChange(e.target.checked)}
                                                />
                                            }
                                            label={"Invoice Visible Time"}
                                        />
                                    )}
                                </form.Field>
                                <form.Field name={"invVisTravel"}>
                                    {(field) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={field.state.value ?? false}
                                                    onChange={(e) => field.handleChange(e.target.checked)}
                                                />
                                            }
                                            label={"Invoice Visible Travel Expenses"}
                                        />
                                    )}
                                </form.Field>
                                <form.Field name={"invVisIndvExp"}>
                                    {(field) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={field.state.value ?? false}
                                                    onChange={(e) => field.handleChange(e.target.checked)}
                                                />
                                            }
                                            label={"Invoice Visible Individual Expenses"}
                                        />
                                    )}
                                </form.Field>
                                <form.Field name={"invVisOffExp"}>
                                    {(field) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={field.state.value ?? false}
                                                    onChange={(e) => field.handleChange(e.target.checked)}
                                                />
                                            }
                                            label={"Invoice Visible Office Expenses"}
                                        />
                                    )}
                                </form.Field>
                            </Box>
                        </Grid2>
                        <Grid2 size={6}>
                            <form.Field name={"costRt"}>
                                {(field) => (
                                    <CurrencyInput
                                        type={"number"}
                                        label={"Default Cost Rate"}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e)}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                        </Grid2>
                        <Grid2 size={6}>
                            <form.Field name={"billingRt"}>
                                {(field) => (
                                    <CurrencyInput
                                        type={"number"}
                                        label={"Default Billing Rate"}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e)}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                        </Grid2>
                        <Grid2 size={6}>
                            <form.Field name={"internalRt"}>
                                {(field) => (
                                    <CurrencyInput
                                        type={"number"}
                                        label={"Default Internal Rate"}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e)}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                        </Grid2>
                    </Grid2>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={onClose}>
                        Close
                    </Button>
                    <Button type={"submit"} variant={"contained"}>
                        Save
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
